import { roleAtLeast } from '@/lib/roles';

/** One rail entry → scrolls to section `id`, labelled by `settings.nav.<key>`. */
export interface RailItem { id: string; key: string }
/** A labelled group of rail items → header from `settings.group.<key>`. */
export interface RailGroup { key: string; items: RailItem[] }

/**
 * De volgorde van de organisatie-pagina.
 *
 * Stond op objectnaam (Klant, Abonnement, Controlemomenten, …) en dus stond het
 * grootste blok — Abonnement, 676px aan pakketkaarten — bovenaan terwijl je het
 * zelden aanraakt. De koppen zijn nu vragen die je zelf stelt, en de volgorde is
 * hoe vaak je er komt.
 *
 * Persoonlijke voorkeuren staan hier niet meer tussen: die verhuisden naar
 * Mijn account (`accountGroups`), zodat het scherm zelf vertelt of iets voor
 * jou of voor de hele organisatie geldt.
 */
const BASE: RailGroup[] = [
  {
    key: 'doing',
    items: [
      { id: 'scan-schedule', key: 'scanSchedule' },
      { id: 'integrations', key: 'webshop' },
    ],
  },
  { key: 'access', items: [] },
  {
    key: 'output',
    items: [
      // Not admin-gated like the full export: a report is a view of numbers the
      // user already sees, narrowed to a period.
      { id: 'reports', key: 'reports' },
    ],
  },
  {
    key: 'org',
    items: [
      { id: 'tenant', key: 'tenant' },
      { id: 'subscription', key: 'subscription' },
    ],
  },
];

/** Grouped rail model, filtered by role.
 *
 *  De gevarenzone staat nu WEL in de rail. Hij hing onderaan de lange pagina
 *  zonder eigen ingang; met één onderwerp per scherm zou hij daarmee
 *  onbereikbaar worden. */
export interface VisibleOptions {
  /** The AI-app connector is switched on for this organisation (GET /connect/status). */
  aiApps?: boolean;
}

export function visibleGroups(role: string, opts: VisibleOptions = {}): RailGroup[] {
  const groups = BASE.map((g) => ({ ...g, items: [...g.items] }));
  const access = groups.find((g) => g.key === 'access')!;
  const output = groups.find((g) => g.key === 'output')!;

  if (roleAtLeast(role, 'supervisor')) {
    access.items.push({ id: 'team', key: 'team' });
    access.items.push({ id: 'activity', key: 'activity' });
  }
  if (roleAtLeast(role, 'admin')) {
    // A key opens the organisation's data under its own name, so handing one
    // out is an act of the organisation -- same gate as the export.
    access.items.push({ id: 'api-keys', key: 'apiKeys' });
    // The full export is admin-only server-side (it hands over the whole
    // dataset); showing the entry to anyone else buys a 403 and a support mail.
    output.items.push({ id: 'data-export', key: 'dataExport' });
  }
  // Every role: a viewer may connect an app that only reads. Hidden while the
  // connector is off for this organisation (rollout switch, spec 2026-11-02 §9).
  if (opts.aiApps) access.items.push({ id: 'ai-apps', key: 'aiApps' });
  output.items.push({ id: 'webhooks', key: 'webhooks' });
  // Beheerder, net als beide acties erin ("Organisatie verlaten" en "Koppeling
  // verbreken") van een echte klant al vragen. Hij stond hier voor iedereen, en
  // dus ook in een meekijksessie, die rol viewer draagt: dan is de duurste knop
  // van de applicatie in beeld tijdens een supportgesprek. De alleen-lezen-grens
  // weigert hem, maar aanbieden wat je niet mag is geen aanbod.
  if (roleAtLeast(role, 'admin')) {
    groups.push({ key: 'danger', items: [{ id: 'danger', key: 'danger' }] });
  }

  return groups.filter((g) => g.items.length > 0);
}

/** Mijn account — alles wat alleen voor de ingelogde persoon geldt. */
export function accountGroups(): RailGroup[] {
  return [
    {
      key: 'me',
      items: [
        { id: 'login', key: 'login' },
        { id: 'notifications', key: 'notifications' },
        { id: 'appearance', key: 'appearance' },
      ],
    },
  ];
}

/** Waar een gevonden instelling woont: op de pagina of in de account-overlay. */
export type SettingHome = 'settings' | 'account';

export interface SearchHit { id: string; key: string; where: SettingHome }

/**
 * Alles wat je kunt zoeken, uit beide ingangen tegelijk.
 *
 * Zoeken op alleen de elf kopjes die al in de rail staan heeft geen zin — je
 * leest ze sneller dan je typt. Het nut zit in de trefwoorden per sectie
 * (`settings.keywords.<id>`): "donker" hoort bij Weergave, "opzeggen" bij de
 * gevarenzone, en die staan op twee verschillende plekken. Precies wat je niet
 * weet als je zoekt.
 */
export function searchableItems(role: string, opts: VisibleOptions = {}): SearchHit[] {
  const here: SearchHit[] = visibleGroups(role, opts).flatMap((g) =>
    g.items.map((i) => ({ id: i.id, key: i.key, where: 'settings' as const })),
  );
  const mine: SearchHit[] = accountGroups().flatMap((g) =>
    g.items.map((i) => ({ id: i.id, key: i.key, where: 'account' as const })),
  );
  return [...here, ...mine];
}
